// API Configuration and utilities
import secureStorage from '../utils/secureStorage';
import { isTokenExpired } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:7000/api' : '/api');

// Public endpoints should never trigger global auth-expired redirects on 401.
const PUBLIC_AUTH_ENDPOINTS = [
  '/auth/signin',
  '/auth/signup',
  '/auth/verify-email',
  '/auth/verify-email-code',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/resend-verification-email',
  '/password/check-strength',
];

const SERVICE_UNAVAILABLE_EVENT = 'service:unavailable';
const SERVICE_UNAVAILABLE_STATUSES = new Set([502, 503, 504]);

const isPublicAuthEndpoint = (endpoint = '') => {
  const normalizedEndpoint = endpoint.split('?')[0];
  return PUBLIC_AUTH_ENDPOINTS.some((publicPath) => normalizedEndpoint.startsWith(publicPath));
};

const notifyServiceUnavailable = (statusCode = 503) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname === '/service-unavailable') {
    return;
  }

  window.dispatchEvent(new CustomEvent(SERVICE_UNAVAILABLE_EVENT, {
    detail: { statusCode },
  }));
};

const shouldRedirectForServiceUnavailable = (response) => {
  if (!response) {
    return false;
  }

  // 502/504 are generally upstream/proxy failures and safe to treat as outage.
  if (response.status === 502 || response.status === 504) {
    return true;
  }

  // 503 can be returned by business logic; only redirect for gateway/fallback HTML responses.
  if (response.status !== 503) {
    return false;
  }

  const markerHeader = response.headers.get('x-service-unavailable') || response.headers.get('x-keyta-service-unavailable');
  if (markerHeader && ['1', 'true', 'yes'].includes(String(markerHeader).toLowerCase())) {
    return true;
  }

  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('text/html');
};

const maybeHandleServiceUnavailable = (response, options = {}) => {
  if (options.skipServiceUnavailableHandling) {
    return;
  }

  if (SERVICE_UNAVAILABLE_STATUSES.has(response.status) && shouldRedirectForServiceUnavailable(response)) {
    notifyServiceUnavailable(response.status);
  }
};

const toReadableErrorText = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    const messages = value
      .map((item) => {
        if (!item) {
          return '';
        }

        if (typeof item === 'string') {
          return item.trim();
        }

        if (typeof item === 'object') {
          const rawMessage = (item.msg || item.message || '').toString().trim();
          const loc = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : null;
          if (rawMessage && loc) {
            return `${loc}: ${rawMessage}`;
          }
          if (rawMessage) {
            return rawMessage;
          }
        }

        return '';
      })
      .filter(Boolean);

    return messages.join('; ');
  }

  if (typeof value === 'object') {
    if (typeof value.message === 'string' && value.message.trim()) {
      return value.message.trim();
    }

    const detailText = toReadableErrorText(value.detail);
    if (detailText) {
      return detailText;
    }

    const errorsText = toReadableErrorText(value.errors);
    if (errorsText) {
      return errorsText;
    }
  }

  return '';
};

const normalizeApiErrorMessage = (message, errors = [], response = null) => {
  const candidates = [
    message,
    errors,
    response,
  ];

  for (const candidate of candidates) {
    const text = toReadableErrorText(candidate);
    if (text) {
      return text;
    }
  }

  return 'Request failed';
};

// Custom error class for API errors
class ApiError extends Error {
  constructor(message, errors = [], statusCode = null, response = null) {
    const normalizedMessage = normalizeApiErrorMessage(message, errors, response);
    super(normalizedMessage);
    this.name = 'ApiError';
    this.errors = errors;
    this.statusCode = statusCode;
    // The same number as `status` too: callers read either name (chatService sets `status`).
    this.status = statusCode;
    this.response = response;
  }
}

// Helper function to handle auth expiration
const handleAuthExpiration = (response) => {
  if (response.status === 401) {
    // Dispatch logout action through a global event
    window.dispatchEvent(new CustomEvent('auth:expired'));
    console.warn('Authentication expired');
  }
};

// --- Silent refresh machinery ---
let _refreshPromise = null; // Singleton to prevent concurrent refresh calls

/**
 * Rotate the session via the httpOnly refresh cookie.
 * Returns true if the backend issued a new access cookie, false otherwise.
 */
const attemptSilentRefresh = () => secureStorage.refreshSession();

/**
 * Wrapper that ensures only one refresh call is in-flight at a time.
 */
const silentRefresh = () => {
  if (!_refreshPromise) {
    _refreshPromise = attemptSilentRefresh().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
};

// Helper function to check token before requests.
// If the access token is expired but a refresh token exists, attempt a
// silent refresh instead of immediately logging the user out.
const checkTokenExpiration = async () => {
  const token = secureStorage.getToken();
  
  if (token && token !== 'cookie-auth' && isTokenExpired && isTokenExpired(token)) {
    // Try to silently obtain a new access token first
    const refreshed = await silentRefresh();
    if (!refreshed) {
      window.dispatchEvent(new CustomEvent('auth:expired'));
      throw new ApiError('Unauthorized - Token expired', [], 401);
    }
    // Token was refreshed successfully — the caller will re-read the token
  }
};

// Helper function to process API responses (returns data only)
const processApiResponse = (responseData, response) => {
  // Expect new ApiResponse pattern only
  if (responseData && typeof responseData === 'object' && 'success' in responseData) {
    if (responseData.success) {
      // Success response
      // If 'data' property exists, return it (standard format)
      if ('data' in responseData) {
        if (responseData.data === null || responseData.data === undefined) {
          responseData.data = {}; // Normalize null/undefined data to empty object 
        }
        // If message exists in the root response, attach it to the data object if it's an object
        if (responseData.message && typeof responseData.data === 'object' && !Array.isArray(responseData.data)) {
          responseData.data.message = responseData.message;
        }
        return responseData.data;
      }
      // If no 'data' property, return the whole object (custom format like form assistance)
      return responseData;
    } else {
      // Error response - throw ApiError with structured errors
      const errorMessage = responseData.message || 'API request failed';
      const errors = responseData.errors || [];
      throw new ApiError(errorMessage, errors, response.status, responseData);
    }
  } else {
    return responseData;
    // If response doesn't follow new format, throw error
    // throw new ApiError('Invalid API response format', [], response.status, responseData);
  }
};

// Helper function to process API responses (returns full response)
const processApiResponseRaw = (responseData, response) => {
  // Expect new ApiResponse pattern only
  if (responseData && typeof responseData === 'object' && 'success' in responseData) {
    if (responseData.success) {
      // Success response - return the full response object
      return responseData;
    } else {
      // Error response - throw ApiError with structured errors
      const errorMessage = responseData.message || 'API request failed';
      const errors = responseData.errors || [];
      throw new ApiError(errorMessage, errors, response.status, responseData);
    }
  } else {
    return responseData;
    // If response doesn't follow new format, throw error
    // throw new ApiError('Invalid API response format', [], response.status, responseData);
  }
};

// Common API instance with default configuration
const api = {
  baseURL: API_BASE_URL,
  
  // Helper method for authenticated requests
  authRequest: async (endpoint, options = {}) => {
    const isPublicEndpoint = isPublicAuthEndpoint(endpoint) || options.skipAuthHandling === true;

    // Check token expiration before making request (may trigger silent refresh)
    if (!isPublicEndpoint) {
      await checkTokenExpiration();
    }
    
    const token = isPublicEndpoint ? null : secureStorage.getToken();

    // Handle Query Parameters (mimic axios params)
    let url = `${API_BASE_URL}${endpoint}`;
    if (options.params) {
      const queryParams = new URLSearchParams();
      Object.keys(options.params).forEach(key => {
        if (options.params[key] !== undefined && options.params[key] !== null) {
          queryParams.append(key, options.params[key]);
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
      // Remove params from options so it doesn't get passed to fetch (though fetch ignores it)
      delete options.params; 
    }
    
    // Handle different authentication methods
    const authHeaders = {};
    if (token === 'cookie-auth') {
      // For production with httpOnly cookies, don't add Authorization header
      // Cookies will be sent automatically with credentials: 'include'
    } else if (token) {
      authHeaders['Authorization'] = `Bearer ${token}`;
    }
    
    // Build headers - only set Content-Type if not explicitly disabled
    const headers = { ...authHeaders };
    
    // If options.headers exists and has 'Content-Type': null or 'Content-Type': undefined,
    // don't set default Content-Type (useful for FormData uploads)
    if (options.headers && ('Content-Type' in options.headers)) {
      // User explicitly provided Content-Type (even if null/undefined to disable it)
      if (options.headers['Content-Type']) {
        headers['Content-Type'] = options.headers['Content-Type'];
      }
      // If Content-Type is null/undefined, don't set it (browser will set it for FormData)
    } else {
      // Default to JSON for backward compatibility
      headers['Content-Type'] = 'application/json';
    }
    
    // Merge other headers (excluding Content-Type which we already handled)
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        if (key !== 'Content-Type') {
          headers[key] = options.headers[key];
        }
      });
    }
    
    let response;

    try {
      response = await fetch(url, {
        ...options,
        credentials: 'include', // Important for cookie-based auth - equivalent to withCredentials: true
        headers,
      });
    } catch (error) {
      throw error;
    }
    
    // On 401 for protected endpoints, attempt a single silent refresh and retry the request
    if (response.status === 401 && !isPublicEndpoint) {
      const refreshed = await silentRefresh();
      if (refreshed) {
        // Re-read token in case it was updated (sessionStorage fallback)
        const newToken = secureStorage.getToken();
        const retryHeaders = { ...headers };
        if (newToken && newToken !== 'cookie-auth') {
          retryHeaders['Authorization'] = `Bearer ${newToken}`;
        } else {
          delete retryHeaders['Authorization'];
        }

        const retryResponse = await fetch(url, {
          ...options,
          credentials: 'include',
          headers: retryHeaders,
        });

        // If retry still fails with 401 there's nothing more we can do
        if (retryResponse.status === 401) {
          handleAuthExpiration(retryResponse);
        }
        maybeHandleServiceUnavailable(retryResponse, options);
        return retryResponse;
      }
      // Refresh failed — expire session
      handleAuthExpiration(response);
    }

    maybeHandleServiceUnavailable(response, options);
    
    return response;
  },
  
  // Helper method for regular requests (also with credentials for consistency)
  request: async (endpoint, options = {}) => {
    // Handle Query Parameters
    let url = `${API_BASE_URL}${endpoint}`;
    if (options.params) {
      const queryParams = new URLSearchParams();
      Object.keys(options.params).forEach(key => {
        if (options.params[key] !== undefined && options.params[key] !== null) {
          queryParams.append(key, options.params[key]);
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
      delete options.params;
    }

    // Build headers - only set Content-Type if not explicitly disabled
    const headers = {};
    
    // If options.headers exists and has 'Content-Type': null or 'Content-Type': undefined,
    // don't set default Content-Type (useful for FormData uploads)
    if (options.headers && ('Content-Type' in options.headers)) {
      // User explicitly provided Content-Type (even if null/undefined to disable it)
      if (options.headers['Content-Type']) {
        headers['Content-Type'] = options.headers['Content-Type'];
      }
      // If Content-Type is null/undefined, don't set it (browser will set it for FormData)
    } else {
      // Default to JSON for backward compatibility
      headers['Content-Type'] = 'application/json';
    }
    
    // Merge other headers (excluding Content-Type which we already handled)
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        if (key !== 'Content-Type') {
          headers[key] = options.headers[key];
        }
      });
    }
    
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include', // Include credentials for all requests
        headers,
      });

      maybeHandleServiceUnavailable(response, options);
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Convenience methods for common HTTP verbs (authenticated) - returns data only
  get: async (endpoint, options = {}) => {
    const response = await api.authRequest(endpoint, { ...options, method: 'GET' });
    
    if (!response.ok) {
      // Auth errors are already handled in authRequest
      const errorData = await response.json().catch(() => ({ 
        detail: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed',
        message: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed'
      }));
      
      // Handle both old and new error formats
      if (errorData.success === false) {
        // New format error
        throw new ApiError(errorData.message, errorData.errors, response.status, errorData);
      } else {
        // Legacy format error
        const message = errorData.detail || errorData.message || 
                       (response.status === 401 ? 'Unauthorized' : 
                        response.status === 403 ? 'Forbidden' : 'Request failed');
        throw new ApiError(message, [], response.status);
      }
    }
    
    const responseData = await response.json();
    return processApiResponse(responseData, response);
  },

  // Raw methods that return full API response (including meta, errors, etc.)
  getRaw: async (endpoint, options = {}) => {
    const response = await api.authRequest(endpoint, { ...options, method: 'GET' });
    
    if (!response.ok) {
      // Auth errors are already handled in authRequest
      const errorData = await response.json().catch(() => ({ 
        detail: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed',
        message: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed'
      }));
      
      // Handle both old and new error formats
      if (errorData.success === false) {
        // New format error
        throw new ApiError(errorData.message, errorData.errors, response.status, errorData);
      } else {
        // Legacy format error
        const message = errorData.detail || errorData.message || 
                       (response.status === 401 ? 'Unauthorized' : 
                        response.status === 403 ? 'Forbidden' : 'Request failed');
        throw new ApiError(message, [], response.status);
      }
    }
    
    const responseData = await response.json();
    return processApiResponseRaw(responseData, response);
  },

  post: async (endpoint, data, options = {}) => {
    const response = await api.authRequest(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      // Auth errors are already handled in authRequest
      const errorData = await response.json().catch(() => ({ 
        detail: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed',
        message: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed'
      }));
      
      // Handle both old and new error formats
      if (errorData.success === false) {
        // New format error
        throw new ApiError(errorData.message, errorData.errors, response.status, errorData);
      } else {
        // Legacy format error
        const message = errorData.detail || errorData.message || 
                       (response.status === 401 ? 'Unauthorized' : 
                        response.status === 403 ? 'Forbidden' : 'Request failed');
        throw new ApiError(message, [], response.status);
      }
    }
    
    const responseData = await response.json();
    return processApiResponse(responseData, response);
  },

  postRaw: async (endpoint, data, options = {}) => {
    const response = await api.authRequest(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      // Auth errors are already handled in authRequest
      const errorData = await response.json().catch(() => ({ 
        detail: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed',
        message: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed'
      }));
      
      // Handle both old and new error formats
      if (errorData.success === false) {
        // New format error
        throw new ApiError(errorData.message, errorData.errors, response.status, errorData);
      } else {
        // Legacy format error
        const message = errorData.detail || errorData.message || 
                       (response.status === 401 ? 'Unauthorized' : 
                        response.status === 403 ? 'Forbidden' : 'Request failed');
        throw new ApiError(message, [], response.status);
      }
    }
    
    const responseData = await response.json();
    return processApiResponseRaw(responseData, response);
  },

  put: async (endpoint, data, options = {}) => {
    const response = await api.authRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      // Auth errors are already handled in authRequest
      const errorData = await response.json().catch(() => ({ 
        detail: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed',
        message: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed'
      }));
      
      // Handle both old and new error formats
      if (errorData.success === false) {
        // New format error
        throw new ApiError(errorData.message, errorData.errors, response.status, errorData);
      } else {
        // Legacy format error
        const message = errorData.detail || errorData.message || 
                       (response.status === 401 ? 'Unauthorized' : 
                        response.status === 403 ? 'Forbidden' : 'Request failed');
        throw new ApiError(message, [], response.status);
      }
    }
    
    const responseData = await response.json();
    return processApiResponse(responseData, response);
  },

  putRaw: async (endpoint, data, options = {}) => {
    const response = await api.authRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      // Auth errors are already handled in authRequest
      const errorData = await response.json().catch(() => ({ 
        detail: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed',
        message: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed'
      }));
      
      // Handle both old and new error formats
      if (errorData.success === false) {
        // New format error
        throw new ApiError(errorData.message, errorData.errors, response.status, errorData);
      } else {
        // Legacy format error
        const message = errorData.detail || errorData.message || 
                       (response.status === 401 ? 'Unauthorized' : 
                        response.status === 403 ? 'Forbidden' : 'Request failed');
        throw new ApiError(message, [], response.status);
      }
    }
    
    const responseData = await response.json();
    return processApiResponseRaw(responseData, response);
  },

  delete: async (endpoint, options = {}) => {
    const response = await api.authRequest(endpoint, { ...options, method: 'DELETE' });
    
    if (!response.ok) {
      // Auth errors are already handled in authRequest
      const errorData = await response.json().catch(() => ({ 
        detail: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed',
        message: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed'
      }));
      
      // Handle both old and new error formats
      if (errorData.success === false) {
        // New format error
        throw new ApiError(errorData.message, errorData.errors, response.status, errorData);
      } else {
        // Legacy format error
        const message = errorData.detail || errorData.message || 
                       (response.status === 401 ? 'Unauthorized' : 
                        response.status === 403 ? 'Forbidden' : 'Request failed');
        throw new ApiError(message, [], response.status);
      }
    }
    
    // Gracefully handle 204 No Content / empty bodies (common for DELETE)
    if (response.status === 204) {
      return {};
    }
    const rawText = await response.text();
    if (!rawText) {
      return {};
    }
    let responseData;
    try {
      responseData = JSON.parse(rawText);
    } catch {
      return {};
    }
    return processApiResponse(responseData, response);
  },

  deleteRaw: async (endpoint, options = {}) => {
    const response = await api.authRequest(endpoint, { ...options, method: 'DELETE' });
    
    if (!response.ok) {
      // Auth errors are already handled in authRequest
      const errorData = await response.json().catch(() => ({ 
        detail: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed',
        message: response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Forbidden' : 'Request failed'
      }));
      
      // Handle both old and new error formats
      if (errorData.success === false) {
        // New format error
        throw new ApiError(errorData.message, errorData.errors, response.status, errorData);
      } else {
        // Legacy format error
        const message = errorData.detail || errorData.message || 
                       (response.status === 401 ? 'Unauthorized' : 
                        response.status === 403 ? 'Forbidden' : 'Request failed');
        throw new ApiError(message, [], response.status);
      }
    }
    
    if (response.status === 204) {
      return { success: true };
    }
    const rawText = await response.text();
    if (!rawText) {
      return { success: true };
    }
    let responseData;
    try {
      responseData = JSON.parse(rawText);
    } catch {
      return { success: true };
    }
    return processApiResponseRaw(responseData, response);
  }
};

// Export both the api object and the ApiError class
export { ApiError };
export default api;